<template>
  <div ref="CRENEAUX">
    <div class="ps-4">
      <section-header ref="CRENEAUX"
                      :dialog="true"
                      :ressource="ressource"
                      @close-dialog="closeDialog">
      </section-header>
    </div>

    <!-- Sélection multi-dates -->
    <div v-if="canEdit" class="px-4 pb-2 creneaux-multidate-block">
      <div class="creneaux-multidate-label edit-field-label">Sélectionner une ou plusieurs dates</div>
      <v-locale-provider locale="fr">
        <v-date-picker
          class="creneaux-multidate"
          multiple
          v-model="pickedDates"
          :min="minDate"
          :max="maxDate"
          :first-day-of-week="1"
          hide-header
          show-adjacent-months></v-date-picker>
        <!-- Astérisque sur les jours déjà saisis : VDatePicker ne transmet pas le slot
             #day à son VDatePickerMonth interne, on décore donc les cellules via leur
             attribut data-v-date (format local YYYY-MM-DD identique à Vuetify). -->
        <component :is="'style'">{{ asteriskCss }}</component>
      </v-locale-provider>
    </div>

    <!-- Saisie des horaires : appliqués à toutes les dates sélectionnées -->
    <div v-if="canEdit" class="px-4 pt-2 horaires-block">
      <div class="horaires-title">Horaires pour les jours sélectionnés</div>

      <div v-for="(h, hi) in horaires" :key="hi" class="horaire-card">
        <div class="horaire-card-body">
          <div class="horaire-row">
            <div class="horaire-label edit-field-label">Horaires</div>
            <div class="horaire-fields horaire-fields-heure">
              <div class="heure-inputs">
                <creneau-heure-debut :ref="'HEURE_DEBUT' + hi" :value="h.heureDebut" :hide-label="true" :context-id="900000 + hi"
                                     @update:value="(v) => setHoraireDebut(v, hi)" @status="setHeureStatus"></creneau-heure-debut>
                <creneau-heure-fin :value="h.heureFin" :hide-label="true" :context-id="910000 + hi"
                                   @update:value="(v) => setHoraireFin(v, hi)" @status="setHeureStatus"></creneau-heure-fin>
              </div>
              <p v-if="horaireError(h, hi)" class="text-12 text-bold error-text horaire-error-inline">{{ horaireError(h, hi) }}</p>
            </div>
          </div>
          <div class="horaire-row">
            <div class="horaire-label edit-field-label">Mode de rémunération</div>
            <div class="horaire-fields">
              <activite-select :value="h.activiteId"
                               :contrat="contrat"
                               :ressource="activiteRessource"
                               :list-activite-id="listActiviteId"
                               :index="hi"
                               :toggle-reset="resetActivity"
                               :context-id="920000 + hi"
                               :required="true"
                               :read-only="false"
                               :hide-label="true"
                               @update:value="(v) => setHoraireActivite(v, hi)"></activite-select>
            </div>
          </div>
          <div class="horaire-row">
            <div class="horaire-label edit-field-label">Lieu</div>
            <div class="horaire-fields horaire-fields-lieu">
              <lieu-summary :value="h.lieuId"
                            parent-type="Contrat"
                            :owner="owner"
                            :fiche="fiche"
                            :ressource="ressourceAdresse"
                            section-name="CreneauxEditor"
                            :context-id="930000 + hi"
                            :hide-label="true"
                            :required="false"
                            :read-only="false"
                            @update:value="(v) => setHoraireLieu(v, hi)"></lieu-summary>
            </div>
          </div>
        </div>
        <div v-if="horaires.length > 1" class="horaire-divider"></div>
        <v-icon v-if="horaires.length > 1" class="horaire-trash" size="22" @click="removeHoraire(hi)">fas fa-trash-can</v-icon>
      </div>

      <div class="creneaux-actions mt-40">
        <div class="horaire-add-btn" @click="addHoraire">
          <v-icon class="back-icon">fas fa-plus</v-icon>
          <div class="ps-2" style="font-size: 12px">Ajouter un horaire</div>
        </div>
        <loader-button v-if="canEdit"
                       type="submit"
                       :loading="loading"
                       :label="saveLabel"
                       :disabled="!disableSave"
                       @callback="save">
        </loader-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { storeCreneau, storeActivite, storeLieu } from '@/generated/stores';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import ActiviteSelect from '@/extend/controls/activite/ActiviteSelect';
  import LieuSummary from '@/extend/controls/lieu/LieuSummary';
  import Contrat from '@/extend/classes/Contrat';
  import { ContratStatuts } from '@/generated/classes/Contrat';
  import Creneau from '@/extend/classes/Creneau';
  import { Alerts } from '@/framework/base/helpers/Alerts';
  import Dialog from '@/generated/classes/Dialog';
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import { FieldStatus } from '@/framework/base/components/FieldInfo';
  import { AppSections, FwkButtons, RessourceAppSection, RessourceFwkButton } from '@/ressources/globalRessources';
  import { RessourceGenControl, GenControl } from '@/generated/ressources/generatedRessources';
  import CreneauHeureDebut from '@/extend/controls/creneau/CreneauHeureDebut';
  import CreneauHeureFin from '@/extend/controls/creneau/CreneauHeureFin';
  import Owner from '@/extend/classes/Owner';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { defineComponent, type PropType } from "vue";

  interface Horaire {
    heureDebut: string | null;
    heureFin: string | null;
    activiteId: number;
    lieuId: number;
    // Validité de format (« Heure invalide ») de chaque champ, alimentée par @status.
    // Portée par l'objet lui-même pour rester correcte après suppression/réordonnancement.
    heureDebutError?: boolean;
    heureFinError?: boolean;
  }

  interface PreviewCreneau {
    date: Date;
    heureDebut: string | null;
    heureFin: string | null;
    activiteId: number;
    lieuId: number;
  }

  export default defineComponent({
    name: 'CreneauxEditorPanel',
    components: {
      LoaderButton,
      SectionHeader,
      ActiviteSelect,
      LieuSummary,
      CreneauHeureDebut,
      CreneauHeureFin,
    },
    props: {
      contrat: {
        type: Object as PropType<Contrat>,
        required: true,
      },
      creneaux: {
        type: Array as PropType<Creneau[]>,
        required: false,
      },
      creneauId: {
        type: Number,
        required: false,
      },
      closeSection: {
        type: Boolean,
        required: false,
      },
      fiche: {
        type: String,
        required: false,
      },
      editCreneau: {
        // Jour à éditer (créneau groupé avec ses plages). Absent => mode ajout.
        type: Object as PropType<any>,
        required: false,
      },
    },
    data() {
      const minDate = null as Date | null;
      const maxDate = null as Date | null;
      return {
        minDate: minDate,
        maxDate: maxDate,
        pickedDates: [] as Date[],
        horaires: [] as Horaire[],
        // Créneaux DÉJÀ enregistrés marqués pour suppression (remplacement d'une date unique
        // par une date d'un autre mois). Réellement supprimés à l'enregistrement (save()).
        pendingDeleteIds: [] as number[],
        resetActivity: true,
        loading: false,
        saving: false,
        initialState: '',
        editedDayKey: null as string | null,
      };
    },
    computed: {
      activiteRessource(): any {
        return RessourceGenControl(GenControl.CRENEAUX_ACTIVITE_SELECT);
      },
      ressourceAdresse(): any {
        return RessourceGenControl(GenControl.CRENEAUX_LIEU_SUMMARY);
      },
      saveLabel(): any {
        return RessourceFwkButton(FwkButtons.ENREGISTRER_CRENEAUX).label;
      },
      ressource(): any {
        return RessourceAppSection(AppSections.CRENEAUX);
      },
      owner(): Owner {
        const owner = new Owner();
        owner.objType = ObjTypes.CONTRAT;
        owner.objId = this.contrat.id;
        return owner;
      },
      editedCreneauIds(): number[] {
        // En ÉDITION, ids des créneaux du jour modifié (déjà représenté par pickedDates /
        // les horaires). Sert à ne pas le compter/contraindre une seconde fois. Vide en ajout.
        return (this.editCreneau && this.editCreneau.plages)
          ? this.editCreneau.plages.map((p: any) => p.creneauId)
          : [];
      },
      listActiviteId(): number[] {
        // Modes de rémunération des horaires en cours de saisie + ceux des créneaux DÉJÀ
        // enregistrés du contrat. Permet à compatibleCi98 (ActiviteSelect) de n'autoriser
        // que la famille cohérente : si l'existant est en cachets, on ne propose que les
        // cachets ; s'il est en heures, on ne propose que les heures (et inversement).
        const fromHoraires = this.horaires.map((h) => h.activiteId);
        // En édition, le jour modifié est déjà représenté par les horaires : on l'exclut
        // des créneaux existants pour ne pas se contraindre soi-même (s'il est seul, 5 choix).
        const fromExisting = storeCreneau.listCreneau
          .filter((c) => c.contratId == this.contrat.id && c.activiteId > 0
            && !this.editedCreneauIds.includes(c.id) && !this.pendingDeleteIds.includes(c.id))
          .map((c) => c.activiteId);
        return [...fromHoraires, ...fromExisting];
      },
      previewHeaders(): any {
        return [
          { title: "Date", key: "date", sortable: false },
          { title: "Horaires", key: "horaires", sortable: false },
          { title: "Mode de rémunération", key: "activiteId", sortable: false },
          { title: "Lieu", key: "lieuId", sortable: false },
        ];
      },
      previewCreneaux(): PreviewCreneau[] {
        const res: PreviewCreneau[] = [];
        const dates = [...this.pickedDates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        dates.forEach((date) => {
          this.horaires.filter((h) => this.horaireComplete(h)).forEach((h) => {
            res.push({
              date,
              heureDebut: h.heureDebut,
              heureFin: h.heureFin,
              activiteId: h.activiteId,
              lieuId: h.lieuId,
            });
          });
        });
        return res;
      },
      disableSave(): boolean {
        return this.canEdit
          && this.pickedDates.length > 0
          && this.horaires.length > 0
          && this.horaires.every((h) => !!h.heureDebut && !!h.heureFin && h.activiteId > 0 && this.heuresValid(h))
          && !this.hasHeureFormatError()
          && !this.hasOverlap();
      },
      canEdit(): boolean {
        return (this.contrat.statut < ContratStatuts.SUBMITTED || this.contrat.statut == ContratStatuts.MODIFIED);
      },
      previewTitle(): string {
        return this.editedDayKey ? 'Créneaux du jour' : 'Créneaux à ajouter';
      },
      creneauIsoDates(): string[] {
        // Jours (format local YYYY-MM-DD, identique à l'attribut data-v-date de Vuetify)
        // où le contrat a déjà au moins un créneau saisi (date renseignée, hors 1901).
        // Le jour en cours d'édition est EXCLU (déjà représenté par pickedDates : il ne doit
        // ni compter en double dans le verrou de mois, ni afficher un astérisque redondant).
        const pad = (n: number): string => String(n).padStart(2, '0');
        const res: string[] = [];
        storeCreneau.listCreneau.forEach((c) => {
          if (c.contratId != this.contrat.id
            || this.pendingDeleteIds.includes(c.id)
            || this.editedCreneauIds.includes(c.id)) {
            return;
          }
          const d = new Date(c.date);
          if (d.getFullYear() === 1901) {
            return;
          }
          const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          if (!res.includes(iso)) {
            res.push(iso);
          }
        });
        return res;
      },
      asteriskCss(): string {
        const dates = this.creneauIsoDates;
        if (dates.length === 0) {
          return '';
        }
        const selectors = dates
          .map((d) => `.creneaux-multidate .v-date-picker-month__day[data-v-date="${d}"]::after`)
          .join(',');
        return (
          '.creneaux-multidate .v-date-picker-month__day{position:relative;}' +
          selectors +
          '{content:"*";position:absolute;top:2px;right:8px;font-weight:700;font-size:14px;' +
          'line-height:1;color:rgb(var(--v-theme-secondary40));pointer-events:none;z-index:2;}'
        );
      },
    },
    watch: {
      pickedDates(newVal: Date[], oldVal: Date[]): void {
        this.enforceSingleMonthReplace(newVal, oldVal);
        this.syncMonthRange();
      },
    },
    created(): void {
      if (this.editCreneau && this.editCreneau.plages) {
        // Mode ÉDITION : pré-remplir avec le jour sélectionné (date + ses plages).
        const d = new Date(this.editCreneau.date);
        this.editedDayKey = d.toDateString();
        this.pickedDates.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
        this.editCreneau.plages.forEach((p: any) => {
          this.horaires.push({
            heureDebut: p.heureDebut,
            heureFin: p.heureFin,
            activiteId: p.activiteId,
            lieuId: p.lieuId,
          });
        });
        if (this.horaires.length === 0) {
          this.horaires.push(this.emptyHoraire());
        }
      } else {
        // Mode AJOUT : on démarre à vide (on n'affiche pas les créneaux déjà saisis).
        this.horaires.push(this.emptyHoraire());
      }
      // Verrouille le mois sur les créneaux existants si le contrat en a déjà (règle 1 mois).
      this.syncMonthRange();
      // Instantané de l'état à l'ouverture (pour détecter toute modification à la fermeture)
      this.initialState = this.stateSnapshot();
    },
    beforeUnmount(): void {
      // À la fermeture : si l'état a changé (dates et/ou horaires) et que rien n'a été
      // enregistré, proposer la boîte de dialogue Enregistrer / Ignorer (comme ailleurs).
      if (this.closeSection || this.saving) {
        return;
      }
      if (this.stateSnapshot() !== this.initialState) {
        Alerts.saveOrCancel().then((result: boolean) => {
          if (result) {
            this.save();
          } else {
            this.$emit('cancelled');
          }
        });
      }
    },
    methods: {
      horaireComplete(h: Horaire): boolean {
        // Champs obligatoires d'un horaire (le lieu n'est pas obligatoire).
        return !!h.heureDebut && !!h.heureFin && h.activiteId > 0;
      },
      stateSnapshot(): string {
        // Représentation comparable de l'état enregistrable (dates + horaires complets),
        // insensible à l'ordre. Les horaires vides/incomplets sont ignorés : ajouter un
        // horaire vide ne compte pas comme une modification (pas de boîte à la fermeture).
        const completes = this.horaires.filter((h) => this.horaireComplete(h));
        const dates = completes.length > 0
          ? this.pickedDates.map((d) => new Date(d).toDateString()).sort()
          : [];
        const horaires = completes
          .map((h) => `${h.heureDebut}|${h.heureFin}|${h.activiteId}|${h.lieuId}`)
          .sort();
        return JSON.stringify({ dates, horaires });
      },
      emptyHoraire(): Horaire {
        return { heureDebut: null, heureFin: null, activiteId: 0, lieuId: 0, heureDebutError: false, heureFinError: false };
      },
      addHoraire(): void {
        this.horaires.push(this.emptyHoraire());
        this.$nextTick(() => {
          const index = this.horaires.length - 1;
          const field = this.$refs['HEURE_DEBUT' + index] as { setFocus?: () => void } | undefined;
          if (field && field.setFocus) {
            field.setFocus();
          }
        });
      },
      removeHoraire(index: number): void {
        this.horaires.splice(index, 1);
        if (this.horaires.length === 0) {
          this.horaires.push(this.emptyHoraire());
        }
      },
      setHoraireDebut(value: string | number, index: number): void {
        this.horaires[index].heureDebut = value != null ? value.toString() : null;
      },
      setHoraireFin(value: string | number, index: number): void {
        this.horaires[index].heureFin = value != null ? value.toString() : null;
      },
      setHoraireActivite(value: number, index: number): void {
        this.horaires[index].activiteId = value;
        if (value > 0) {
          this.resetActivity = !this.resetActivity;
        }
      },
      setHoraireLieu(value: number, index: number): void {
        this.horaires[index].lieuId = value;
      },
      setHeureStatus(status: FieldStatus): void {
        // status.objId = contextId du champ : 900000+hi (début) ou 910000+hi (fin).
        // On stocke l'erreur sur l'objet horaire concerné pour qu'elle suive l'objet en
        // cas de suppression/réordonnancement. Réactif -> hasHeureFormatError -> disableSave.
        const isDebut = status.objId >= 900000 && status.objId < 910000;
        const hi = status.objId - (isDebut ? 900000 : 910000);
        const horaire = this.horaires[hi];
        if (!horaire) {
          return;
        }
        if (isDebut) {
          horaire.heureDebutError = status.error;
        } else {
          horaire.heureFinError = status.error;
        }
      },
      hasHeureFormatError(): boolean {
        return this.horaires.some((h) => h.heureDebutError || h.heureFinError);
      },
      hasInvalidHours(): boolean {
        // Une heure « saisie invalide » = format incorrect (« Heure invalide ») OU
        // heure de fin ≤ heure de début. Exposé pour bloquer la fermeture du volet.
        // Les heures vides/incomplètes ne comptent pas (l'utilisateur peut fermer sans saisir).
        return this.horaires.some((h) =>
          h.heureDebutError || h.heureFinError
          || (!!h.heureDebut && !!h.heureFin && !this.heuresValid(h)));
      },
      heuresValid(h: Horaire): boolean {
        // Heure de fin strictement postérieure à l'heure de début.
        if (!h.heureDebut || !h.heureFin) {
          return true;
        }
        const debut = DateFormat.getHeures(h.heureDebut);
        const fin = DateFormat.getHeures(h.heureFin);
        if (debut == null || fin == null) {
          return true;
        }
        return fin > debut;
      },
      horaireError(h: Horaire, index: number): string {
        if (h.heureDebutError || h.heureFinError) {
          return 'Heure invalide';
        }
        if (h.heureDebut && h.heureFin && !this.heuresValid(h)) {
          return "L'heure de fin doit être postérieure à l'heure de début";
        }
        return this.horaireOverlapError(h, index);
      },
      horaireOverlapError(h: Horaire, index: number): string {
        if (!h.heureDebut || !h.heureFin) {
          return '';
        }
        // vs un autre horaire (les horaires s'appliquent aux mêmes dates)
        if (this.pickedDates.length > 0) {
          for (let j = 0; j < this.horaires.length; j++) {
            if (j === index) continue;
            const o = this.horaires[j];
            if (this.overlaps(h.heureDebut, h.heureFin, o.heureDebut, o.heureFin)) {
              return "Cet horaire en chevauche un autre sur les mêmes dates";
            }
          }
        }
        // vs un créneau déjà saisi sur une date sélectionnée (hors jour en édition,
        // hors créneaux marqués pour suppression).
        const pickedKeys = new Set(this.pickedDates.map((d) => new Date(d).toDateString()));
        const existing = storeCreneau.listCreneau.filter((c) => c.contratId == this.contrat.id
          && !this.pendingDeleteIds.includes(c.id)
          && (!this.editedDayKey || new Date(c.date).toDateString() !== this.editedDayKey));
        for (const e of existing) {
          if (!pickedKeys.has(new Date(e.date).toDateString())) {
            continue;
          }
          // Doublon EXACT (mêmes heures + activité + lieu) : le jour est déjà couvert, on
          // l'ignorera à l'enregistrement -> ce n'est pas un conflit à signaler.
          if (this.isSameCreneau(e, h)) {
            continue;
          }
          if (this.overlaps(h.heureDebut, h.heureFin, e.heureDebut, e.heureFin)) {
            return "Cet horaire chevauche un créneau déjà saisi sur une date sélectionnée";
          }
        }
        return '';
      },
      overlaps(aDebut: string | null, aFin: string | null, bDebut: string | null, bFin: string | null): boolean {
        if (!aDebut || !aFin || !bDebut || !bFin) {
          return false;
        }
        const ad = DateFormat.getHeures(aDebut);
        const af = DateFormat.getHeures(aFin);
        const bd = DateFormat.getHeures(bDebut);
        const bf = DateFormat.getHeures(bFin);
        if (ad == null || af == null || bd == null || bf == null) {
          return false;
        }
        // chevauchement strict (ex. 10:00-13:00 et 13:00-18:00 ne se chevauchent pas)
        return ad < bf && af > bd;
      },
      isSameCreneau(a: { heureDebut: string | null; heureFin: string | null; activiteId: number; lieuId: number },
                    b: { heureDebut: string | null; heureFin: string | null; activiteId: number; lieuId: number }): boolean {
        // Deux créneaux « exactement identiques » : mêmes heures + même activité + même lieu.
        // Un tel doublon (sur la même date) n'est ni un conflit ni à recréer : on l'ignore.
        return a.heureDebut === b.heureDebut && a.heureFin === b.heureFin
          && a.activiteId === b.activiteId && a.lieuId === b.lieuId;
      },
      hasOverlap(): boolean {
        // Créneaux déjà saisis du contrat (par jour + plage horaire), hors jour en édition
        // et hors créneaux marqués pour suppression.
        const existing = storeCreneau.listCreneau
          .filter((c) => c.contratId == this.contrat.id
            && !this.pendingDeleteIds.includes(c.id)
            && (!this.editedDayKey || new Date(c.date).toDateString() !== this.editedDayKey))
          .map((c) => ({ key: new Date(c.date).toDateString(), debut: c.heureDebut, fin: c.heureFin,
                         activiteId: c.activiteId, lieuId: c.lieuId }));
        // Nouveaux créneaux à ajouter (produit dates × horaires)
        const news = this.previewCreneaux
          .map((p) => ({ key: new Date(p.date).toDateString(), debut: p.heureDebut, fin: p.heureFin,
                         activiteId: p.activiteId, lieuId: p.lieuId }));
        for (let i = 0; i < news.length; i++) {
          const n = news[i];
          // vs créneaux existants du même jour
          for (const e of existing) {
            if (e.key !== n.key) {
              continue;
            }
            // Doublon EXACT : ignoré à l'enregistrement -> pas un conflit.
            if (this.isSameCreneau({ heureDebut: e.debut, heureFin: e.fin, activiteId: e.activiteId, lieuId: e.lieuId },
                                   { heureDebut: n.debut, heureFin: n.fin, activiteId: n.activiteId, lieuId: n.lieuId })) {
              continue;
            }
            if (this.overlaps(n.debut, n.fin, e.debut, e.fin)) {
              return true;
            }
          }
          // vs autres nouveaux créneaux du même jour
          for (let j = i + 1; j < news.length; j++) {
            const m = news[j];
            if (m.key === n.key && this.overlaps(n.debut, n.fin, m.debut, m.fin)) {
              return true;
            }
          }
        }
        return false;
      },
      isoDate(d: Date): string {
        // Jour au format local YYYY-MM-DD (identique à l'attribut data-v-date de Vuetify
        // et à creneauIsoDates). Sert à dédoublonner/comparer des dates au jour près.
        const pad = (n: number): string => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      },
      enforceSingleMonthReplace(newVal: Date[], oldVal: Date[]): void {
        // Quand le contrat ne compte qu'UNE SEULE date au total (créneau déjà enregistré OU
        // date déjà cliquée) et que l'utilisateur clique une date d'un AUTRE mois, on
        // REMPLACE cette date unique par la nouvelle au lieu d'en ajouter une seconde
        // (règle « un seul mois par contrat »). Un éventuel créneau enregistré de la date
        // unique est marqué pour suppression (retiré à l'enregistrement, cf. save()).
        const oldPicked = oldVal || [];
        const oldKeys = new Set(oldPicked.map((d) => this.isoDate(new Date(d))));
        const added = newVal.filter((d) => !oldKeys.has(this.isoDate(new Date(d))));
        if (added.length !== 1) {
          return; // on ne traite que l'ajout d'une seule date (ni désélection, ni prégarnissage)
        }
        const newIso = this.isoDate(new Date(added[0]));
        // Dates distinctes AVANT ce clic : créneaux existants actifs ∪ dates déjà cliquées.
        const before = new Set<string>(this.creneauIsoDates);
        oldPicked.forEach((d) => before.add(this.isoDate(new Date(d))));
        if (before.size !== 1) {
          return; // la règle ne s'applique que s'il n'y avait qu'une seule date au total
        }
        const singleIso = [...before][0];
        if (singleIso.slice(0, 7) === newIso.slice(0, 7)) {
          return; // même mois -> comportement normal (on conserve les deux dates)
        }
        // Marquer pour suppression le(s) créneau(x) enregistré(s) de la date unique
        // (aucun s'il s'agissait d'une simple date cliquée)…
        storeCreneau.listCreneau
          .filter((c) => c.contratId == this.contrat.id
            && !this.pendingDeleteIds.includes(c.id)
            && this.isoDate(new Date(c.date)) === singleIso)
          .forEach((c) => this.pendingDeleteIds.push(c.id));
        // …et ne garder que la nouvelle date dans la sélection.
        this.pickedDates = [added[0]];
      },
      syncMonthRange(): void {
        // Règle : un seul mois par contrat. On ne verrouille min/max QUE lorsque le contrat
        // implique PLUS D'UNE date au total — créneaux DÉJÀ enregistrés (creneauIsoDates)
        // ∪ dates en cours de sélection (pickedDates), sans doublon. Avec 0 ou 1 date au
        // total, la navigation entre mois reste libre. Le mois de référence est celui d'un
        // créneau existant s'il y en a, sinon celui de la première date choisie.
        const distinct = new Set<string>(this.creneauIsoDates);
        this.pickedDates.forEach((p) => distinct.add(this.isoDate(new Date(p))));
        let ref: Date | null = null;
        if (distinct.size > 1) {
          const existing = storeCreneau.listCreneau
            .filter((c) => c.contratId == this.contrat.id && !this.pendingDeleteIds.includes(c.id))
            .map((c) => new Date(c.date))
            .filter((d) => d.getFullYear() !== 1901);
          ref = existing.length > 0 ? existing[0] : new Date(this.pickedDates[0]);
        }
        if (ref) {
          this.minDate = new Date(ref.getFullYear(), ref.getMonth(), 1);
          this.maxDate = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
        } else {
          this.minDate = null;
          this.maxDate = null;
        }
      },
      activiteLabel(id: number): string {
        return id > 0 ? storeActivite.getActivite(id).label : '';
      },
      lieuLabel(id: number): string {
        return id > 0 ? storeLieu.getLieu(id).label : '';
      },
      formatDate(date: Date): string {
        return new Date(date).toLocaleDateString('fr-FR');
      },
      save(): void {
        // Filet : beforeUnmount peut appeler save() via Alerts.saveOrCancel même quand le
        // bouton est désactivé (heures invalides, incomplètes…). On refuse alors la sauvegarde.
        if (!this.disableSave) {
          return;
        }
        // Contrôle : aucun chevauchement entre les nouveaux créneaux et les déjà saisis
        // (ni entre nouveaux). Si chevauchement -> on bloque l'enregistrement avec une alerte.
        if (this.hasOverlap()) {
          const dialog = new Dialog();
          dialog.label = "Impossible d'enregistrer : un ou plusieurs créneaux à ajouter chevauchent un créneau déjà saisi sur la même date. Corrigez les horaires avant d'enregistrer.";
          Alerts.message(dialog);
          return;
        }
        this.saving = true;
        this.loading = true;
        // En ÉDITION : retirer du store les créneaux du jour édité (ils sont remplacés
        // par la version modifiée). En AJOUT : on conserve tout l'existant.
        if (this.editedDayKey) {
          storeCreneau.listCreneau
            .filter((c) => c.contratId == this.contrat.id && new Date(c.date).toDateString() === this.editedDayKey)
            .forEach((c) => storeCreneau.mCreneauDeleted(c.id));
        }
        // Remplacement d'une date unique vers un autre mois : retirer le(s) créneau(x) marqué(s).
        this.pendingDeleteIds.forEach((id) => storeCreneau.mCreneauDeleted(id));
        // Ajouter le produit (dates × horaires) — édité ou nouveau — puis envoyer la liste complète.
        // (Les créneaux du jour édité et ceux marqués pour suppression ont déjà été retirés
        // ci-dessus : la liste ne contient donc plus que les créneaux qui vont subsister.)
        this.previewCreneaux.forEach((p) => {
          // Jour déjà couvert par un créneau EXACTEMENT identique -> on n'ajoute rien (pas de doublon).
          const dateKey = new Date(p.date).toDateString();
          const dejaPresent = storeCreneau.listCreneau.some((c) => c.contratId == this.contrat.id
            && new Date(c.date).toDateString() === dateKey && this.isSameCreneau(c, p));
          if (dejaPresent) {
            return;
          }
          const creneau = storeCreneau.createNewCreneau();
          creneau.contratId = this.contrat.id;
          const local = new Date(p.date);
          creneau.date = new Date(local.getTime() - local.getTimezoneOffset() * 60000);
          creneau.heureDebut = p.heureDebut;
          creneau.heureFin = p.heureFin;
          creneau.activiteId = p.activiteId;
          creneau.lieuId = p.lieuId;
        });
        const creneaux = storeCreneau.listCreneau.filter((c) => c.contratId == this.contrat.id);
        Creneau.updateCreneaux(creneaux).then(() => {
          this.loading = false;
          this.$emit('saved');
          this.closeDialog();
        }).catch(() => {
          this.loading = false;
        });
      },
      closeDialog() {
        this.$emit('closeDialog');
      },
    },
  });
</script>

<style scoped>
.creneaux-multidate-block {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.creneaux-multidate-label {
  padding-bottom: 8px;
}

/* Bloc "Horaires pour les jours sélectionnés" */
.horaires-title {
  font-size: 18px;
  font-weight: 400;
  color: rgb(var(--v-theme-grey10));
  margin-bottom: 16px;
}
.horaire-card {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 24px;
}
.horaire-card-body {
  flex: 1 1 auto;
  min-width: 0;
}
.horaire-row {
  display: flex;
  align-items: center;
  margin-bottom: 0px;
}
/* Collapse la zone de messages Vuetify sous chaque champ (espacement excessif) */
.horaire-card-body :deep(.v-input__details) {
  min-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}
/* Champs heure : le message de format (« Heure invalide ») est affiché via
   .horaire-error-inline (à droite des champs). On masque donc la zone de messages native
   (écrasée par le collapse ci-dessus, elle fuyait tronquée) pour éviter un doublon. La
   bordure rouge d'erreur du champ (portée par .v-field--error) reste bien visible. */
.horaire-fields-heure :deep(.v-input__details) {
  display: none;
}
/* Zone de saisie des heures réduite, message d'erreur en flux à droite sur la même ligne. */
.horaire-fields-heure {
  align-items: center;
}
.horaire-fields-heure > .heure-inputs {
  flex: 0 0 auto;
  display: flex;
  gap: 12px;
}
.horaire-fields-heure > .heure-inputs > * {
  flex: 0 0 120px;
  min-width: 0;
}
.horaire-fields-heure > .horaire-error-inline {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
}
.horaire-divider {
  align-self: stretch;
  width: 1px;
  background: rgb(var(--v-theme-grey90));
  /* Marge gauche : réserve l'espace du crayon « Lieu » qui déborde, pour que le trait
     vertical et la corbeille restent à droite des crayons. */
  margin: 4px 0 4px 32px;
}
.horaire-label {
  flex: 0 0 180px;
  padding-right: 8px;
}
.horaire-fields {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 12px;
}
.horaire-fields > * {
  flex: 1 1 0;
  min-width: 0;
}
/* Lieu : le crayon d'édition (append Vuetify) est sorti du flux et déborde à droite,
   afin que la zone de saisie ait la même largeur que le select Mode de rémunération. */
.horaire-fields-lieu :deep(.v-input) {
  position: relative;
}
.horaire-fields-lieu :deep(.v-input__append) {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  padding-inline-start: 6px;
}
.horaire-trash {
  color: rgb(var(--v-theme-secondary40));
  cursor: pointer;
}
.creneaux-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
/* Bouton « Ajouter un horaire » : reprend la couleur et la police du bouton « Retour ». */
.horaire-add-btn {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 8px 16px;
  background-color: #FCE8E8;
  border-radius: 8px;
  cursor: pointer;
}
</style>
